import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Heart, 
  Target, 
  Eye, 
  Award, 
  Shield,
  Users,
  ArrowRight,
  CheckCircle2,
  Star
} from 'lucide-react';

const TEAM_IMAGE = "https://images.pexels.com/photos/6129507/pexels-photo-6129507.jpeg";
const CARE_IMAGE = "https://images.pexels.com/photos/7551608/pexels-photo-7551608.jpeg";

const values = [
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We treat every patient with the care and dignity they deserve, as if they were our own family.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'We maintain the highest standards of clinical quality and continuously improve our services.',
  },
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We operate with honesty and transparency in everything we do for our patients and partners.',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We work together with patients, families, and healthcare providers for the best outcomes.',
  },
];

const accreditations = [
  {
    name: 'Medicare Certified',
    description: 'Fully certified to provide Medicare-covered home health services',
  },
  {
    name: 'CHAP Accredited',
    description: 'Community Health Accreditation Partner - Meeting highest quality standards',
  },
  {
    name: 'ACHC Accredited',
    description: 'Accreditation Commission for Health Care - Quality & patient safety excellence',
  },
];

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#000080]" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#000080] to-[#1E3A8A]"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#34D399] font-semibold mb-4">ABOUT US</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Your Partner for Life
            </h1>
            <p className="text-xl text-white/80">
              Senior Care Solutions is a Medicare certified & CHAP/ACHC accredited skilled home 
              healthcare agency serving Florida with compassion and excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white" data-testid="company-overview">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#00A878] font-semibold mb-4">WHO WE ARE</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Compassionate Care, Professional Excellence
              </h2>
              <p className="text-lg text-[#334155] mb-6 leading-relaxed">
                Senior Care Solutions provides a compassionate multi-disciplined professional health 
                care team who provides skilled in-home health care services & comprehensive health 
                care solutions.
              </p>
              <p className="text-lg text-[#334155] mb-6 leading-relaxed">
                We are advocates on a mission to help our patients & families navigate their 
                individual needs, while providing exceptional care to optimize the best outcomes.
              </p>
              <p className="text-lg text-[#334155] mb-8 leading-relaxed">
                Our team of caring and trusted professionals provide expertise in hospital-to-home 
                transitions, recovery, and the management of acute & chronic illnesses, helping 
                individuals achieve their highest level of health at home.
              </p>

              <div className="space-y-4">
                {['24/7 Clinical on-call support', 
                  'Multi-disciplined professional team', 
                  'Hospital-to-home transition specialists',
                  'Chronic illness management experts',
                  'Reducing risks of hospitalization'].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#00A878]" />
                    <span className="text-[#334155] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src={TEAM_IMAGE}
                alt="Our caring medical team"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="mission-vision">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            <Card className="bg-white border-none shadow-xl overflow-hidden">
              <div className="h-2 bg-[#00A878]"></div>
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-[#ECFDF5] flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-[#00A878]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Mission</h3>
                <p className="text-[#334155] leading-relaxed mb-4">
                  At Senior Care Solutions, we're more than a professional team who provide skilled 
                  home health care services & care solutions for those who need specialized attention, 
                  regardless of where home is.
                </p>
                <p className="text-[#334155] leading-relaxed">
                  We're advocates on a mission to help patients live their very best lives and have 
                  a great care experience with exceptional outcomes in the location that suits them 
                  best—no matter how their needs may change over time.
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="bg-white border-none shadow-xl overflow-hidden">
              <div className="h-2 bg-[#000080]"></div>
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-[#000080]/10 flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-[#000080]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Vision</h3>
                <p className="text-[#334155] leading-relaxed mb-4">
                  Our vision is to become synonymous with compassion and competency in the seamless 
                  integration & delivery of care that follows patients as their needs and health change.
                </p>
                <p className="text-[#334155] leading-relaxed">
                  We aim to become a true partner for life, providing continuous, adaptive care 
                  that grows and evolves with our patients' needs while maintaining the highest 
                  standards of quality and compassion.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white" data-testid="core-values">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#00A878] font-semibold mb-4">OUR VALUES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              The Principles That Guide Us
            </h2>
            <p className="text-lg text-[#334155]">
              Our core values define who we are and how we deliver care to every patient we serve.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card 
                key={value.title}
                className="service-card bg-white border border-slate-100 text-center"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-8 h-8 text-[#00A878]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-3">{value.title}</h3>
                  <p className="text-[#334155] leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-20 bg-[#000080]" data-testid="accreditations">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#34D399] font-semibold mb-4">ACCREDITATIONS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Trusted & Certified Care
            </h2>
            <p className="text-lg text-white/80">
              Our accreditations demonstrate our commitment to meeting the highest standards 
              of quality and patient safety.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {accreditations.map((accred, index) => (
              <div 
                key={accred.name}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center"
                data-testid={`accreditation-${index}`}
              >
                <div className="w-20 h-20 rounded-full bg-[#00A878] flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{accred.name}</h3>
                <p className="text-white/70">{accred.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="why-choose-us">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={CARE_IMAGE}
                alt="Caring for our patients"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>

            <div className="order-1 lg:order-2">
              <span className="inline-block text-[#00A878] font-semibold mb-4">WHY CHOOSE US</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Experience the Senior Care Solutions Difference
              </h2>
              <p className="text-lg text-[#334155] mb-8 leading-relaxed">
                Our services assist seniors with management of their illnesses while optimizing 
                their healthy behaviors to allow them to remain in their homes for as long as 
                possible and reducing their risks of hospitalization and/or placement to higher 
                level of care.
              </p>

              <div className="space-y-6">
                {[
                  { title: 'Personalized Care Plans', desc: 'Tailored to each patient\'s unique needs and goals' },
                  { title: 'Experienced Professionals', desc: 'Highly trained nurses, therapists, and aides' },
                  { title: 'Family-Centered Approach', desc: 'Including families in the care journey' },
                  { title: 'Seamless Coordination', desc: 'Working with physicians and specialists' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#00A878] flex items-center justify-center flex-shrink-0 mt-1">
                      <Star className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0F172A] mb-1">{item.title}</h4>
                      <p className="text-[#334155]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#ECFDF5]" data-testid="about-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
            Ready to Experience Our Care?
          </h2>
          <p className="text-lg text-[#334155] mb-8 max-w-2xl mx-auto">
            Contact us today to learn more about how Senior Care Solutions can help you 
            or your loved one achieve the highest level of health at home.
          </p>
          <Link to="/contact" data-testid="about-cta-button">
            <Button 
              size="lg" 
              className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-semibold"
            >
              Get Started Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
